import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { SharedLayout } from '../components/layout/SharedLayout';
import { theme } from '../styles/theme';
import { resendService } from '../services/resendService';

const PageWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(to bottom, #002f6c, #001f4d);
`;

const HeroSection = styled.section`
  padding: 80px 20px;
  margin-top: -80px;
  padding-top: 120px;
  text-align: center;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const PageTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: bold;
  color: white;
  margin-bottom: 24px;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 2.5rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.5rem;
  color: #a5c9ff;
  max-width: 800px;
  margin: 0 auto 16px;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1.25rem;
  }
`;

const SubtitleSmall = styled.p`
  font-size: 1.25rem;
  color: #a5c9ff;
  margin-bottom: 60px;
`;

const OptionsContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const OptionCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 40px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 24px;
  }
`;

const OptionNumber = styled.h2`
  font-size: 2rem;
  font-weight: bold;
  color: white;
  margin-bottom: 16px;
`;

const OptionDescription = styled.p`
  font-size: 1.125rem;
  color: #a5c9ff;
  margin-bottom: 24px;
  line-height: 1.6;
`;

const CallButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  background: #10b981;
  color: white;
  padding: 16px 32px;
  border-radius: 8px;
  font-size: 1.25rem;
  font-weight: bold;
  text-decoration: none;
  transition: all 0.3s;
  
  &:hover {
    background: #059669;
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(16, 185, 129, 0.3);
  }
`;

const CallNote = styled.span`
  display: block;
  font-size: 1rem;
  color: #a5c9ff;
  font-style: italic;
  margin-top: 12px;
`;

const QuestionContainer = styled.div`
  background: #001f4d;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 20px;
`;

const QuestionTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: white;
  margin-bottom: 16px;
`;

const QuestionBox = styled.pre`
  background: rgba(0, 0, 0, 0.3);
  padding: 20px;
  border-radius: 8px;
  color: #a5c9ff;
  font-family: monospace;
  font-size: 0.95rem;
  white-space: pre-wrap;
  word-wrap: break-word;
  line-height: 1.6;
`;

const CopyButton = styled.button`
  background: #3b82f6;
  color: white;
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    background: #2563eb;
    transform: translateY(-2px);
  }
`;

const EmailForm = styled.form`
  display: flex;
  gap: 16px;
  max-width: 500px;
  margin: 0 auto;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-direction: column;
  }
`;

const EmailInput = styled.input`
  flex: 1;
  padding: 12px 20px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  font-size: 1rem;
  
  &::placeholder {
    color: #a5c9ff;
  }
  
  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.5);
    background: rgba(255, 255, 255, 0.25);
  }
`;

const NotifyButton = styled.button`
  background: #f97316;
  color: white;
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    background: #ea580c;
    transform: translateY(-2px);
  }
`;

const FeaturesSection = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 40px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  margin-top: 60px;
`;

const FeaturesTitle = styled.h3`
  font-size: 2rem;
  font-weight: bold;
  color: white;
  margin-bottom: 32px;
`;

const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 20px;
  color: #a5c9ff;
  font-size: 1.125rem;
  line-height: 1.6;
`;

const CheckIcon = styled.span`
  color: #10b981;
  font-size: 1.5rem;
  margin-top: -4px;
`;

const LaunchDate = styled.div`
  text-align: center;
  margin-top: 60px;
  padding: 24px;
`;

const LaunchText = styled.p`
  font-size: 1.5rem;
  font-weight: bold;
  color: #f97316;
`;

const FooterCTA = styled.div`
  text-align: center;
  padding: 40px 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  margin-top: 60px;
`;

const FooterText = styled.p`
  font-size: 1.125rem;
  color: #a5c9ff;
  
  a {
    color: white;
    font-weight: 600;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const ContactLink = styled.button`
  background: none;
  border: none;
  color: white;
  font-weight: 600;
  font-size: inherit;
  cursor: pointer;
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const ThankYouMessage = styled.div`
  background: #10b981;
  color: white;
  padding: 16px 24px;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  text-align: center;
  animation: slideIn 0.3s ease-out;
  
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

export default function AIBacktester() {
  const [email, setEmail] = useState('');
  const [copied, setCopied] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const quintessentialQuestion = `I am considering [or currently own] a [ANNUITY NAME] from [COMPANY NAME]. 

Based on actual historical performance data and real-world returns (not hypothetical illustrations), what are the key risks, limitations, and realistic return expectations for this specific annuity? Please include any concerns about managed volatility indices, date lottery effects, caps, participation rates, and how actual client returns have compared to illustrated returns.`;

  const handleCopyQuestion = () => {
    navigator.clipboard.writeText(quintessentialQuestion);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  const handleNotifySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Send admin notification email
      await resendService.sendEmail({
        to: 'jake@annuity-answers.com',
        subject: '🚀 New Deep Dive Tool Early Access Request',
        html: `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
            <div style="background-color: #002f6c; padding: 20px; text-align: center;">
              <h1 style="color: white; margin: 0;">Deep Dive Tool Early Access</h1>
            </div>
            <div style="padding: 30px; background-color: #f9f9f9;">
              <h2 style="color: #333;">New Sign-Up Alert!</h2>
              <p style="color: #666; font-size: 16px;">Someone wants early access to the Deep Dive tool:</p>
              
              <div style="background-color: white; padding: 20px; border-radius: 8px; margin: 20px 0;">
                <p style="margin: 10px 0;"><strong>Email:</strong> ${email}</p>
                <p style="margin: 10px 0;"><strong>Date:</strong> ${new Date().toLocaleString()}</p>
                <p style="margin: 10px 0;"><strong>Page:</strong> Deep Dive Tool Landing Page</p>
              </div>
              
              <div style="background-color: #ffd700; padding: 15px; border-radius: 8px; margin-top: 20px;">
                <p style="color: #333; margin: 0; font-weight: bold;">
                  💡 Follow-up Recommended: This user is showing high intent for the Deep Dive tool.
                </p>
              </div>
              
              <p style="color: #999; font-size: 14px; margin-top: 20px;">
                This notification was sent from the Deep Dive Tool page on AnnuityAnswers.com
              </p>
            </div>
          </div>
        `
      });

      // Send thank you email to user
      await resendService.sendEmail({
        to: email,
        subject: 'You\'re on the List! Deep Dive Tool Early Access',
        html: `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
            <div style="background-color: #002f6c; padding: 20px; text-align: center;">
              <h1 style="color: white; margin: 0;">Welcome to the Future of Annuity Analysis</h1>
            </div>
            <div style="padding: 30px;">
              <h2 style="color: #333;">You're In! 🎉</h2>
              <p style="color: #666; font-size: 16px; line-height: 1.6;">
                Thank you for your interest in our Deep Dive tool. You'll be among the first to know when it launches!
              </p>
              
              <div style="background-color: #f9f9f9; padding: 20px; border-radius: 8px; margin: 20px 0;">
                <h3 style="color: #333;">What to Expect:</h3>
                <ul style="color: #666; line-height: 1.8;">
                  <li>Early access to the most advanced annuity analysis tool</li>
                  <li>Test any annuity against 20+ years of real market data</li>
                  <li>See the truth about managed volatility indices</li>
                  <li>Understand the date lottery effect on your returns</li>
                </ul>
              </div>
              
              <p style="color: #666; font-size: 16px;">
                In the meantime, feel free to explore our other tools or give us a call at 
                <strong>800-922-1894</strong> if you have questions about your current annuity.
              </p>
              
              <div style="text-align: center; margin-top: 30px;">
                <a href="https://annuityanswers.com" style="background-color: #ffd700; color: #333; padding: 12px 30px; text-decoration: none; border-radius: 5px; font-weight: bold;">
                  Visit Our Website
                </a>
              </div>
            </div>
          </div>
        `
      });

      setSubmitted(true);
      setEmail('');
      
      // Reset the thank you message after 5 seconds
      setTimeout(() => {
        setSubmitted(false);
      }, 5000);
      
    } catch (error) {
      console.error('Error submitting email:', error);
      alert('There was an error submitting your email. Please try again or call us at 800-922-1894.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SharedLayout>
      <PageWrapper>
        <HeroSection>
          <Container>
            <PageTitle>Deep Dive Tool Launching Soon</PageTitle>
            <Subtitle>
              We're putting the finishing touches on the most advanced annuity analysis tool ever created.
            </Subtitle>
            <SubtitleSmall>While our team completes final testing:</SubtitleSmall>

            <OptionsContainer>
              <OptionCard>
                <OptionNumber>Option 1: Get Your Analysis Today</OptionNumber>
                <OptionDescription>
                  Our experts can run the same analysis manually and explain every detail.
                </OptionDescription>
                <CallButton href="tel:800-922-1894">
                  📞 Call Now: 800-922-1894
                </CallButton>
                <CallNote>Most analyses completed in 15 minutes</CallNote>
              </OptionCard>

              <OptionCard>
                <OptionNumber>Option 2: Ask AI Directly</OptionNumber>
                <OptionDescription>
                  Copy our Quintessential Question and get answers from ChatGPT right now:
                </OptionDescription>
                <QuestionContainer>
                  <QuestionTitle>The Question That Reveals Everything:</QuestionTitle>
                  <QuestionBox>{quintessentialQuestion}</QuestionBox>
                </QuestionContainer>
                <CopyButton onClick={handleCopyQuestion}>
                  {copied ? '✓ Copied!' : 'Copy Question'}
                </CopyButton>
              </OptionCard>

              <OptionCard>
                <OptionNumber>Option 3: Get Notified When Ready</OptionNumber>
                <OptionDescription>
                  Be first to access the tool when it launches:
                </OptionDescription>
                {submitted ? (
                  <ThankYouMessage>
                    ✓ Thank you! You're on the list. We'll notify you as soon as the Deep Dive tool launches!
                  </ThankYouMessage>
                ) : (
                  <EmailForm onSubmit={handleNotifySubmit}>
                    <EmailInput
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      required
                      disabled={isSubmitting}
                    />
                    <NotifyButton type="submit" disabled={isSubmitting}>
                      {isSubmitting ? 'Submitting...' : 'Notify Me'}
                    </NotifyButton>
                  </EmailForm>
                )}
              </OptionCard>

              <FeaturesSection>
                <FeaturesTitle>What Makes Our Deep Dive Tool Different:</FeaturesTitle>
                <FeaturesList>
                  <FeatureItem>
                    <CheckIcon>✓</CheckIcon>
                    <span>Tests EVERY trading day (not just January 1st)</span>
                  </FeatureItem>
                  <FeatureItem>
                    <CheckIcon>✓</CheckIcon>
                    <span>Uses REAL market data since 2010</span>
                  </FeatureItem>
                  <FeatureItem>
                    <CheckIcon>✓</CheckIcon>
                    <span>Exposes the date lottery effect</span>
                  </FeatureItem>
                  <FeatureItem>
                    <CheckIcon>✓</CheckIcon>
                    <span>Shows why managed volatility indices average only 2.99%</span>
                  </FeatureItem>
                </FeaturesList>
              </FeaturesSection>
            </OptionsContainer>

            <FooterCTA>
              <FooterText>
                Questions? Call{' '}
                <a href="tel:800-922-1894">800-922-1894</a>
                {' or '}
                <ContactLink onClick={() => navigate('/contact')}>
                  Contact Us
                </ContactLink>
              </FooterText>
            </FooterCTA>
          </Container>
        </HeroSection>
      </PageWrapper>
    </SharedLayout>
  );
}